import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { FloorClass } from '../../../../../models/floor.class';
import { SelectedFloor } from '../floors.models';
import { DataObject } from '../../../../../models/data-object.models';

@UntilDestroy()
@Component({
  selector: 'sp-floors-actions',
  templateUrl: './floors-actions.component.html',
  styleUrls: ['./floors-actions.component.scss'],
  standalone: false,
})
export class FloorsActionsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() selectedFloorGroup: SelectedFloor[];
  @Input() elevatorSelectedFloors: number[];
  @Input() generalCommonFloorSizeFG: UntypedFormGroup;
  @Input() isMoveFloorOverlayOpen = false;
  @Input() uploadingFloorPlan = false;
  @Input() floorsAboveGround = 0;
  @Input() floorsBelowGround = 0;
  @Output() onFileChange: EventEmitter<{ event: any; floors: SelectedFloor[] }> = new EventEmitter();
  @Output() toggleHiddenFloor: EventEmitter<DataObject[]> = new EventEmitter();
  @Output() moveFloorEmitter: EventEmitter<any> = new EventEmitter();
  @Output() deleteFloorEmitter: EventEmitter<void> = new EventEmitter();
  @Output() updateCommonFloorSize: EventEmitter<any> = new EventEmitter();

  showCommonFloorSize = false;
  disableDeleteFloor = false;
  disableFloorPlan = false;
  disableDotsMenu = false;
  disableShowHideFloor = false;
  disableMoveFloor = false;
  hasFloorPlan = false;
  hideShowLabel = false;

  $moveFloorDisabledTooltip = signal(null);
  $toggleVisibilityDisabledTooltip = signal(null);

  ngOnInit() {
    /* required by angular-l10n */
    this.generalCommonFloorSizeFG.valueChanges.pipe(untilDestroyed(this)).subscribe(() => this.setActionIconsState());

    this.#setToolTips();
  }

  ngOnChanges() {
    this.setActionIconsState();
  }

  ngOnDestroy() {
    /* required by angular-l10n */
  }

  setActionIconsState(): void {
    this.disableDeleteFloor =
      this.generalCommonFloorSizeFG.invalid || (this.floorsAboveGround === 0 && this.floorsBelowGround === 0);
    if (this.uploadingFloorPlan || !this.selectedFloorGroup.length) {
      this.showCommonFloorSize = false;
      this.disableIcons(this.uploadingFloorPlan);
      return;
    }
    this.showCommonFloorSize = true;
    this.disableDotsMenu = false;
    this.hideShowLabel = this.selectedFloorGroup.every(({ floorGroup }) => floorGroup.value.hideFloor);
    this.hasFloorPlan = this.selectedFloorGroup.every(({ floorGroup }) => !!floorGroup.value.floorPlan.link);
    this.disableShowHideFloor = this.selectedFloorGroup.every(({ floorGroup }) =>
      this.elevatorSelectedFloors.includes(floorGroup.value.floorIndex)
    );

    // Will be disabled if there isn't exactly 1 floor selected or if the selected floor has an invalid index (e.g. basement floor with index -1)
    this.disableMoveFloor = this.#isMoveFloorDisabled();
    this.#setToolTips();

    this.disableFloorPlan = this.isMoveFloorOverlayOpen;
    if (this.isMoveFloorOverlayOpen) {
      this.disableIcons(true);
    }
  }

  toggleFloorVisibility(): void {
    this.hideShowLabel = !this.hideShowLabel;
    this.selectedFloorGroup.forEach(({ floorGroup }) => {
      const hideFloorFC = floorGroup.controls.hideFloor;
      hideFloorFC.patchValue(this.hideShowLabel);
      hideFloorFC.markAsDirty();
    });
    this.toggleHiddenFloor.emit(this.selectedFloorGroup);
  }

  move(): void {
    this.disableIcons();
    this.moveFloorEmitter.emit();
  }

  deleteFLoor(): void {
    this.deleteFloorEmitter.emit();
  }

  removeFloorPlan(): void {
    this.selectedFloorGroup.forEach(({ floorGroup }) => {
      const floorPlanFG = floorGroup.controls.floorPlan as UntypedFormGroup;
      const floorPlanId = floorPlanFG.controls.id.value;
      floorPlanFG.reset();
      floorPlanFG.controls.id.setValue(floorPlanId);
      this.hasFloorPlan = false;
      floorGroup.markAsDirty();
      floorGroup.markAsTouched();
    });
  }

  manageFileDialogState(label: HTMLLabelElement): void {
    if (this.disableDotsMenu) {
      label.blur();
      // * Trigger action icons enable/disable state after file modal closes...
      this.setActionIconsState();
      return;
    }
    this.disableIcons(true);
  }

  onSetFloorSpace(): void {
    this.updateCommonFloorSize.emit();
  }

  private disableIcons(allIcons = false): void {
    this.disableFloorPlan = true;
    this.disableShowHideFloor = true;
    this.disableMoveFloor = true;
    if (allIcons) {
      this.disableDeleteFloor = true;
      this.disableDotsMenu = true;
    }
  }

  /**
   * Will be disabled if there isn't exactly 1 floor selected or if the selected floor has an invalid index (e.g. basement floor with index -1)
   * @returns boolean
   */
  #isMoveFloorDisabled(): boolean {
    const allInvalidIndexes = this.selectedFloorGroup.every(
      ({ floorGroup }) => !FloorClass.isFloorIndexValid(floorGroup.value.floorIndex)
    );
    // Move floor will be disabled if there isn't exactly 1 floor selected or if the selected floor has an invalid index (e.g. basement floor with index -1)
    return this.selectedFloorGroup.length !== 1 || !allInvalidIndexes;
  }

  get aboveAndUnderFloor() {
    return {
      minUnderGround: FloorClass.getMaxNumberOfFloorsUnderGround() * -1,
      maxAboveGround: FloorClass.getMaxNumberOfFloorsAboveGround(),
    };
  }

  #setToolTips() {
    let moveFloorTooltip = '';
    if (this.#isMoveFloorDisabled()) {
      moveFloorTooltip = `moreInfo.moveFloorMsg`;
    }
    this.$moveFloorDisabledTooltip.set(moveFloorTooltip);

    let toggleVisibilityTooltip = '';
    if (this.disableShowHideFloor && this.selectedFloorGroup.length > 0) {
      const allElevatorFloors = this.selectedFloorGroup.every(({ floorGroup }) =>
        this.elevatorSelectedFloors.includes(floorGroup.value.floorIndex)
      );
      if (allElevatorFloors) {
        toggleVisibilityTooltip = 'moreInfo.hideFloorTooltip';
      }
    }
    this.$toggleVisibilityDisabledTooltip.set(toggleVisibilityTooltip);
  }
}
